import { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
// Recharts used to mimic Blood Pressure Trend style for the "line" option
import {
  ResponsiveContainer as ReResponsive,
  LineChart as ReLineChart,
  Line as ReLine,
  CartesianGrid as ReCartesianGrid,
  XAxis as ReXAxis,
  YAxis as ReYAxis,
  Tooltip as ReTooltip,
  ReferenceArea as ReReferenceArea,
  ReferenceLine as ReReferenceLine,
  Label as ReLabel
} from 'recharts';
import {
  PieChart,
  Users,
  Activity,
  Calendar,
  Clock,
  DollarSign,
  TrendingUp,
  TrendingDown,
  ChartColumn,
  ChartSpline,
} from 'lucide-react';
import {  Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  PointElement,
  LineElement,
} from 'chart.js';
import {
  FaCalendarCheck,
  FaClock,
  FaUserMd,
} from 'react-icons/fa';
import { FaX } from 'react-icons/fa6';
import api from '../../../api';
import visitTypesList from '../../../utils/constants/visitTypes.json';
import { useSelector } from 'react-redux';
import { arClass } from '../../../utils/useArClass';
import textTranslator from '../../../utils/useTextTranslator';
import Txt from '../../Text';
import typeTranslator from '../../../utils/useTypeTranslator';
import DateInput from '../../form/DateInput';
import CustomSelect from '../../CustomSelect';

const Text = ({ children, className }) => (
  <Txt page="STATISTICS" className={className} keyName="STATISTICS">
    {children}
  </Txt>
);

// Register ChartJS components
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  PointElement,
  LineElement
);

// StatCard Component
const StatCard = ({ icon, title, value, subtitle, trend, darkMode, color }) => {
  const colorClasses = {
    blue: darkMode ? 'from-blue-900/20 to-blue-800/20 text-blue-400' : 'from-blue-50 to-blue-100 text-blue-600',
    green: darkMode ? 'from-emerald-900/20 to-emerald-800/20 text-emerald-400' : 'from-emerald-50 to-emerald-100 text-emerald-600',
    purple: darkMode ? 'from-purple-900/20 to-purple-800/20 text-purple-400' : 'from-purple-50 to-purple-100 text-purple-600',
    amber: darkMode ? 'from-amber-900/20 to-amber-800/20 text-amber-400' : 'from-amber-50 to-amber-100 text-amber-600',
    red: darkMode ? 'from-red-900/20 to-red-800/20 text-red-400' : 'from-red-50 to-red-100 text-red-600',
  };

  return (
    <motion.div
      whileHover={{ y: -5, transition: { duration: 0.2 } }}
      className={arClass(`p-5 rounded-xl border backdrop-blur-sm ${
        darkMode
          ? 'bg-gradient-to-br from-gray-800/50 to-gray-900/50 border-gray-700'
          : 'bg-gradient-to-br from-white to-gray-50 border-gray-200'
      }`)}
    >
      <div className={arClass("flex items-start justify-between")}>
        <div>
          <div className={arClass(`inline-flex p-3 rounded-xl ${colorClasses[color]}`)}>
            {icon}
          </div>
          <h3 className={arClass(`mt-4 text-sm max-sm:text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`)}>
            {title}
          </h3>
          <p className={arClass(`mt-2 text-2xl max-sm:text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`)}>
            {value.toLocaleString()}
          </p>
          <p className={arClass(`mt-1 text-sm max-sm:text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`)}>
            {subtitle}
          </p>
        </div>
        {trend && (
          <div className={arClass(`flex items-center gap-1 px-2 py-1 rounded-lg text-sm font-medium ${
            trend.value >= 0
              ? darkMode ? 'bg-emerald-900/30 text-emerald-400' : 'bg-emerald-100 text-emerald-700'
              : darkMode ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-700'
          }`)}>
            {trend.value >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
            <span>{Math.abs(trend.value)}%</span>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// ChartCard Component
const ChartCard = ({ title, description, icon, children, darkMode, height = 'h-72' }) => {
  return (
    <div className={arClass(`p-5 md:p-6 rounded-xl border ${
      darkMode
        ? 'bg-gradient-to-br from-gray-800/50 to-gray-900/50 border-gray-700'
        : 'bg-gradient-to-br from-white to-gray-50 border-gray-200'
    }`)}>
      <div className={arClass("flex items-center justify-between mb-6")}>
        <div>
          <div className={arClass("flex items-center gap-3 mb-2")}>
            <div className={arClass(`p-2 rounded-lg ${
              darkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-600'
            }`)}>
              {icon}
            </div>
            <h3 className={arClass(`text-lg max-sm:text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`)}>
              {title}
            </h3>
          </div>
          <p className={arClass(`text-sm max-sm:text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`)}>
            {description}
          </p>
        </div>
      </div>
      <div className={height}>
        {children}
      </div>
    </div>
  );
};


export default function VisitsStatistics() {
  const darkMode = useSelector((state) => state.settings.darkMode);
  const defaultLanguage = useSelector((state) => state.settings.defaultLanguage);
  // mode: 'day' | 'month' | 'year'
  const [mode, setMode] = useState('month');
  const today = new Date();
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth() + 1);
  const [selectedDate, setSelectedDate] = useState(today.toISOString().slice(0, 10));
  const [years] = useState(() => {
    const y = today.getFullYear();
    return Array.from({ length: 6 }).map((_, i) => y - (5 - i));
  });
  let yearsOptions = () => years.map((y) => ({ value: y, label: y.toString() }));
  let monthsOptions = () =>
    Array.from({ length: 12 }).map((_, i) => {
      const m = i + 1;
      return { value: m, label: new Date(0, i).toLocaleString(defaultLanguage, { month: "long" }) };
    });

  const [monthlyChartType, setMonthlyChartType] = useState('bar');
  const [loading, setLoading] = useState(false);

  // server response state
  const [serverData, setServerData] = useState(null);
  const [stats, setStats] = useState({
    count: 0,
    completedVisits: 0,
    upcomingVisits: 0,
    cancelledVisits: 0,
    averageDuration: 0,
    totalRevenue: 0,
    visitTypes: {},
    departments: {},
    timeSlots: {},
    monthlyVisits: {},
    monthlyTrend: 0,
    patientSatisfaction: 0,
    noShowRate: 0,
  });

  // Chart data calculations
  const chartColors = useMemo(() => [
    "#8B5CF6", "#10B981", "#F59E0B", "#EF4444", "#3B82F6",
    "#EC4899", "#14B8A6", "#F97316", "#6366F1", "#84CC16"
  ], []);

  const gradientColors = useMemo(() => [
    ["#8B5CF6", "#7C3AED"],
    ["#10B981", "#059669"],
    ["#F59E0B", "#D97706"],
    ["#EF4444", "#DC2626"],
    ["#3B82F6", "#2563EB"],
  ], []);

  const createGradient = (ctx, color1, color2) => {
    const gradient = ctx.createLinearGradient(0, 0, 0, 400);
    gradient.addColorStop(0, color1);
    gradient.addColorStop(1, color2);
    return gradient;
  };

  const visitTypeChartData = useMemo(() => {
    const ctx = document.createElement('canvas').getContext('2d');
    // Build labels and data by comparing server visitTypes to local visitTypesList
    const serverMap = stats.visitTypes || {};
    const labels = [];
    const data = [];
    const matchedServerKeys = new Set();

    // First, follow the order in visitTypesList
    visitTypesList.forEach((vt) => {
      const name = vt.nameEn;
      // Exact match
      if (serverMap.hasOwnProperty(name)) {
        labels.push(name);
        data.push(serverMap[name]);
        matchedServerKeys.add(name);
        return;
      }
      // Case-insensitive match
      const key = Object.keys(serverMap).find(k => k.toLowerCase() === name.toLowerCase());
      if (key) {
        labels.push(name);
        data.push(serverMap[key]);
        matchedServerKeys.add(key);
      }
    });

    // Add any remaining server keys that weren't matched to known visit types
    Object.keys(serverMap).forEach((k) => {
      if (!matchedServerKeys.has(k)) {
        labels.push(k);
        data.push(serverMap[k]);
      }
    });

    const bgColors = labels.map((_, i) => {
      const g = gradientColors[i % gradientColors.length];
      return createGradient(ctx, g[0], g[1]);
    });

    const translatedLabels = labels.map((l) => {
      const tt = typeTranslator(l);
      return (tt && tt.translatedType) || textTranslator(l, 'STATISTICS');
    });

    return {
      labels: translatedLabels,
      datasets: [{
        data,
        backgroundColor: bgColors,
        borderWidth: 2,
        borderColor: darkMode ? 'rgb(31, 41, 55)' : 'white',
        hoverBorderWidth: 3,
        hoverBorderColor: darkMode ? 'rgb(55, 65, 81)' : 'rgb(243, 244, 246)',
        borderRadius: 12,
        spacing: 6,
      }]
    };
  }, [stats.visitTypes, darkMode, gradientColors]);

  const departmentChartData = useMemo(() => {
    const ctx = document.createElement('canvas').getContext('2d');
    return {
      labels: ['Cardiology', 'Neurology', 'Pediatrics', 'Orthopedics', 'Dermatology'].map(l => textTranslator(l, 'STATISTICS')),
      datasets: [{
        data: Object.values(stats.departments),
        backgroundColor: gradientColors.slice(0, 5).map(([c1, c2]) => 
          createGradient(ctx, c1, c2)
        ),
        borderWidth: 2,
        borderColor: darkMode ? 'rgb(31, 41, 55)' : 'white',
        borderRadius: 12,
      }]
    };
  }, [stats.departments, darkMode, gradientColors]);

  const monthlyVisitsData = useMemo(() => {
    const ctx = document.createElement('canvas').getContext('2d');
    // Prefer server data if available
    const labels = serverData?.labels?.length ? serverData.labels : ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const dataset = serverData?.series?.[0]?.data?.length ? serverData.series[0].data : labels.map(l => stats.monthlyVisits?.[l] || 0);

    return {
      labels,
      datasets: [{
        label: textTranslator('visits','STATISTICS'),
        data: dataset,
        backgroundColor: createGradient(ctx, '#8B5CF6', '#7C3AED'),
        borderColor: '#8B5CF6',
        borderWidth: 2,
        borderRadius: 8,
        fill: true,
        tension: 0.4,
      }]
    };
  }, [stats.monthlyVisits, darkMode, serverData]);

  const chartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          color: darkMode ? '#E5E7EB' : '#4B5563',
          font: { family: 'Inter, sans-serif', size: 12 },
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle',
        }
      },
      tooltip: {
        backgroundColor: darkMode ? 'rgba(17, 24, 39, 0.95)' : 'rgba(255, 255, 255, 0.95)',
        titleColor: darkMode ? '#F3F4F6' : '#111827',
        bodyColor: darkMode ? '#D1D5DB' : '#374151',
        borderColor: darkMode ? '#374151' : '#E5E7EB',
        borderWidth: 1,
        cornerRadius: 8,
        padding: 12,
        callbacks: {
          label: (context) => {
            const label = context.label || '';
            const value = context.raw || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
            return `${label}: ${value} (${percentage}%)`;
          }
        }
      }
    },
    animation: {
      animateScale: true,
      animateRotate: true,
      duration: 1000,
      easing: 'easeOutQuart'
    },
    elements: {
      arc: {
        borderWidth: 2,
        borderColor: darkMode ? 'rgb(31, 41, 55)' : 'white'
      }
    }
  };

  const barChartOptions = {
    ...chartOptions,
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: darkMode ? 'rgba(55, 65, 81, 0.3)' : 'rgba(229, 231, 235, 0.5)',
          drawBorder: false
        },
        ticks: {
          color: darkMode ? '#9CA3AF' : '#6B7280',
          font: { family: 'Inter, sans-serif' },
          align: defaultLanguage === 'ar' ? 'end' : 'start'
        },
        position: defaultLanguage === 'ar' ? 'right' : 'left'
      },
      x: {
        grid: { display: false },
        ticks: {
          color: darkMode ? '#9CA3AF' : '#6B7280',
          font: { family: 'Inter, sans-serif' }
        },
        reverse: defaultLanguage === 'ar'
      }
    },
    plugins: {
      ...chartOptions.plugins,
      legend: { display: false }
    }
  };


  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const params = { mode };
        if (mode === 'year') params.year = selectedYear;
        if (mode === 'month') {
          params.year = selectedYear;
          params.month = selectedMonth;
        }
        if (mode === 'day') params.date = selectedDate;
      
        const res = await api.get('/statistics/visits', { params });
        const d = res?.data ?? null;
        setServerData(d);

        // Map server response into stats shape used by UI
        const mapped = {
          count: d?.total ?? d?.metrics?.totalVisits ?? 0,
          completedVisits: d?.metrics?.completedVisits ?? d?.metrics?.summary?.completed ?? 0,
          upcomingVisits: 0,
          cancelledVisits: d?.metrics?.visitsTrend?.cancelled ?? 0,
          averageDuration: d?.metrics?.avgDurationMinutes ?? 0,
          totalRevenue: d?.metrics?.totalRevenue ?? 0,
          visitTypes: d?.metrics?.visitTypes ?? {},
          departments: {},
          timeSlots: (d?.metrics?.timeSlotDistribution || []).reduce((acc, cur) => {
            const hour = Number(cur.hour);
            // bucket into morning/afternoon/evening/night
            if (hour >= 6 && hour < 12) acc.morning = (acc.morning || 0) + cur.count;
            else if (hour >= 12 && hour < 18) acc.afternoon = (acc.afternoon || 0) + cur.count;
            else if (hour >= 18 && hour < 22) acc.evening = (acc.evening || 0) + cur.count;
            else acc.night = (acc.night || 0) + cur.count;
            return acc;
          }, {}),
          monthlyVisits: {},
          monthlyTrend: d?.metrics?.visitsTrend?.changePercent ?? 0,
          patientSatisfaction: d?.metrics?.patientSatisfaction ?? 0,
          noShowRate: d?.metrics?.noShowRate ?? 0,
        };

        // If server returned labels/series, populate monthlyVisits
        if (d?.labels && d?.series && Array.isArray(d.series) && d.series[0]) {
          const labels = d.labels;
          const dataArr = d.series[0].data || [];
          const monthlyObj = {};
          labels.forEach((lab, idx) => { monthlyObj[lab] = dataArr[idx] ?? 0; });
          mapped.monthlyVisits = monthlyObj;
        }

        setStats(mapped);
      } catch (err) {
        // Handle error silently
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [mode, selectedYear, selectedMonth, selectedDate]);
  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className={arClass(`flex items-center justify-center p-8 rounded-2xl h-[600px] ${
          darkMode 
            ? 'bg-gradient-to-br from-gray-800 to-gray-900' 
            : 'bg-gradient-to-br from-gray-50 to-white'
        }`)}
      >
        <div className="text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ repeat: Infinity, duration: 1.5, ease: "linear" }}
            className={arClass(`w-16 h-16 border-4 rounded-full border-t-transparent mx-auto ${
              darkMode ? 'border-blue-500' : 'border-blue-600'
            }`)}
          />
          <p className={arClass(`mt-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`)}>
            <Text>Loading visits analytics...</Text>
          </p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className={arClass(`relative p-4 sm:p-6 rounded-2xl ${
        darkMode 
          ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' 
          : 'bg-gradient-to-br from-gray-50 via-white to-gray-50'
      }  border ${darkMode ? 'border-gray-800' : 'border-gray-200'}`)}
    >
      {/* Header */}
      <div className={arClass("flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8")}>
        <div className={arClass("flex items-center gap-4")}>
          <div className={arClass(`p-3 rounded-xl ${
            darkMode 
              ? 'bg-gradient-to-br from-blue-900/30 to-indigo-900/30 text-blue-400' 
              : 'bg-gradient-to-br from-blue-50 to-indigo-50 text-blue-600'
          }`)}>
            <PieChart size={24} />
          </div>
          <div>
            <h1 className={arClass(`text-2xl max-sm:text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`)}>
              <Text>visitsAnalytics</Text>
            </h1>
              <p className={arClass(`text-sm max-sm:text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`)}>
              <Text>visitsOverview</Text>
            </p>
          </div>
        </div>
        
        <div className={arClass("flex items-center gap-3 max-sm:flex-col max-sm:items-stretch max-sm:w-full")}>
            <div className={arClass(`flex items-center gap-2 max-sm:w-full max-sm:justify-between`)}>
            {['day', 'month', 'year'].map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={arClass(`p-3 text-sm rounded-md font-medium transition-colors border w-full sm:w-auto max-sm:text-xs max-sm:p-2 ${
                  mode === m
                    ? 'bg-blue-600 text-white'
                    : darkMode
                    ? 'bg-gray-700 border-gray-300 text-white hover:bg-gray-900'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                }`)}
              >
                <Text>{m.charAt(0).toUpperCase() + m.slice(1)}</Text>
              </button>
            ))}
          </div>

          {/* Dynamic selectors based on mode */}
          <div
            className={arClass(
              `flex items-center gap-2 max-sm:w-full max-sm:justify-between`
            )}
          >
            {mode === "year" && (
              <CustomSelect
                    options={yearsOptions()}
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                    allowCreate={false}
                    allowSearch={false}
                    darkMode={darkMode}
                    className="min-w-[150px] max-sm:min-w-max"
                  />
            
            )}

            {mode === "month" && (
              <>
                <CustomSelect
                    options={yearsOptions()}
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                    allowCreate={false}
                    allowSearch={false}
                    darkMode={darkMode}
                    className="min-w-[150px] max-sm:min-w-max"
                  />

                <CustomSelect
                  options={monthsOptions()}
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  allowCreate={false}
                  allowSearch={false}
                  darkMode={darkMode}
                  className="min-w-[150px] max-sm:min-w-max"
                />
              </>
            )}

            {mode === "day" && (
              <DateInput
                value={selectedDate}
                onChange={(e) => {
                  if (
                    selectedDate !== e.target.value &&
                    e.target.value !== ""
                  ) {
                    setSelectedDate(e.target.value);
                    const parts = e.target.value.split("-");
                    if (parts.length === 3) {
                      setSelectedYear(Number(parts[0]));
                      setSelectedMonth(Number(parts[1]));
                    }
                  }
                }}
                name="date-input" // Add a name for form handling
                className={`p-2 rounded-md border text-sm w-full max-sm:text-xs min-w-[300px] max-sm:min-w-max ${
                  darkMode
                    ? "bg-gray-700 text-white border-gray-300 hover:bg-gray-900"
                    : "bg-white text-gray-700 border-gray-300"
                }`}
                placeholder={
                  defaultLanguage === "en"
                    ? "Select date"
                    : defaultLanguage === "ar"
                    ? "اختر تاريخًا"
                    : "Sélectionner une date"
                }
                // Optional: Add minDate and maxDate constraints
                // minDate="2024-01-01"
                // maxDate="2024-12-31"
                format="YYYY-MM-DD" // Match your current date format
                showTodayButton={true}
                allowClear={true}
                // If you're using React Hook Form, pass these props:
                // register={register}
                // registerProp="dateFieldName"
              />
            )}
          </div>
        </div>
      </div>

      {stats.count === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={arClass(`p-12 rounded-xl text-center ${
            darkMode ? 'bg-gray-800/50' : 'bg-gray-50'
          }`)}
        >
          <div className={arClass(`w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center ${
            darkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-200 text-gray-500'
          }`)}>
            <Users size={32} />
          </div>
          <h3 className={`text-xl font-semibold mb-2 ${
            darkMode ? 'text-gray-300' : 'text-gray-700'
          }`}>
            <Text>noVisitDataTitle</Text>
          </h3>
          <p className={`mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <Text>scheduleVisitsMessage</Text>
          </p>
          <button className={arClass(`px-6 py-2.5 rounded-lg font-medium ${
            darkMode 
              ? 'bg-blue-600 hover:bg-blue-700 text-white' 
              : 'bg-blue-600 hover:bg-blue-700 text-white'
          } transition-colors`)}>
            <Text>scheduleFirstVisit</Text>
          </button>
        </motion.div>
      ) : (
        <>
          {/* Stats Grid */}
          <div className={arClass("grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8")}>
            <StatCard
              icon={<Calendar size={20} />}
              title={<Text>totalVisits</Text>}
              value={stats.count}
              subtitle={<Text>allTimeVisits</Text>}
              trend={{ value: stats.monthlyTrend }}
              darkMode={darkMode}
              color="blue"
            />
            
            <StatCard
              icon={<Activity size={20} />}
              title={<Text>completedVisits</Text>}
              value={stats.completedVisits}
              subtitle={`${((stats.completedVisits / stats.count) * 100).toFixed(1)}% ${textTranslator('completionRate','STATISTICS')}`}
              trend={{ value: 5.2 }}
              darkMode={darkMode}
              color="green"
            />
            
            <StatCard
              icon={<Clock size={20} />}
              title={<Text>avgDuration</Text>}
              value={stats.averageDuration}
              subtitle={<Text>minutesPerVisit</Text>}
              darkMode={darkMode}
              color="purple"
            />
            
            <StatCard
              icon={<DollarSign size={20} />}
              title={<Text>totalRevenue</Text>}
              value={stats.totalRevenue}
              subtitle={<Text>fromVisits</Text>}
              trend={{ value: 15.7 }}
              darkMode={darkMode}
              color="amber"
            />
          </div>

          {/* Main Charts: split into two columns - left doughnut, right breakdown */}
          <div className={arClass("grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8")}>
            {/* Left column: Doughnut chart */}
            <ChartCard
              title={<Text>visitTypes</Text>}
              description={<Text>visitTypesDescription</Text>}
              icon={<FaUserMd size={20} />}
              darkMode={darkMode}
              height="h-[420px] md:h-[580px]"
            >
              <div className={arClass("h-full flex items-center justify-center")}>
                <div className={arClass("w-full max-w-3xl h-[460px] flex items-center justify-center")}>
                  <Doughnut data={visitTypeChartData} options={chartOptions} />
                </div>
              </div>
            </ChartCard>

            {/* Right column: Breakdown list (replaces old Department Analysis slot) */}
            <ChartCard
              title={<Text>visitTypesBreakdown</Text>}
              description={<Text>visitTypesBreakdownDesc</Text>}
              icon={<FaUserMd size={20} />}
              darkMode={darkMode}
              height="h-[420px] md:h-[580px]"
            >
              <div className="h-full overflow-y-auto p-2">
                <div className="flex flex-col gap-3">
                  {visitTypeChartData.labels.map((label, index) => {
                        const value = visitTypeChartData.datasets?.[0]?.data?.[index] ?? 0;
                        const percentage = stats.count > 0 ? (value / stats.count) * 100 : 0;
                        const pctText = percentage.toFixed(1);
                        const color = gradientColors[index % gradientColors.length]?.[0] || chartColors[index % chartColors.length];
                        return (
                          <div key={label} className={arClass(`p-3 rounded-lg border ${darkMode ? 'bg-gray-800/30 border-gray-700' : 'bg-white border-gray-100'}`)}>
                            <div className="flex items-center justify-between mb-2">
                              <div className={arClass(`text-sm max-sm:text-xs font-medium ${darkMode ? 'text-gray-200' : 'text-gray-800'}`)}>{label}</div>
                              <div className={arClass(`text-sm max-sm:text-xs font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`)}>{Number(value || 0).toLocaleString()}</div>
                            </div>
                            <div className={arClass(`h-2 rounded-full overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`)}>
                              <div style={{ width: `${percentage}%`, background: color }} className="h-full rounded-full transition-width" />
                            </div>
                            <div className={arClass("text-xs max-sm:text-[11px] mt-2 text-right text-gray-500")} >{pctText}%</div>
                          </div>
                        );
                      })}
                </div>
              </div>
            </ChartCard>
          </div>

          {/* Bottom Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartCard
              title={<Text>monthlyVisitsTrend</Text>}
              description={<Text>visitVolumeOverTime</Text>}
              icon={<FaCalendarCheck size={20} />}
              darkMode={darkMode}
              height="h-64 md:h-72"
            >
              <div className={arClass("w-full h-full flex flex-col ")}>
                
                <div className={arClass("flex items-center space-x-2 mb-2")}>
                                  <div
                                    className={arClass(
                                      "flex  rounded-lg p-1 gap-1 border border-gray-100"
                                    )}
                                  >
                                    <button
                                      onClick={() => setMonthlyChartType("bar")}
                                      className={arClass(
                                        `p-2 text-sm rounded-md transition-colors  ${
                                          darkMode
                                            ? "bg-slate-600/80 hover:text-white"
                                            : "bg-slate-200 hover:text-black"
                                        } ${
                                          monthlyChartType === "bar" && darkMode
                                            ? "!bg-green-600/80 !text-green-200 border-green-200"
                                            : monthlyChartType === "bar"
                                            ? "!bg-green-200 !text-green-900"
                                            : "text-gray-600  hover:text-gray-900 "
                                        }`
                                      )}
                                    >
                                      <ChartColumn size={20} className="inline-block ml-1" />
                                    </button>
                                    <button
                                      onClick={() => setMonthlyChartType("line")}
                                      className={arClass(
                                        `p-2 text-sm rounded-md transition-colors  ${
                                          darkMode
                                            ? "bg-slate-600/80 hover:text-white"
                                            : "bg-slate-200 hover:text-black"
                                        } ${
                                          monthlyChartType === "line" && darkMode
                                            ? "!bg-green-600/80 !text-green-200 border-green-200"
                                            : monthlyChartType === "line"
                                            ? "!bg-green-200 !text-green-900"
                                            : "text-gray-600  hover:text-gray-900 "
                                        }`
                                      )}
                                    >
                                      <ChartSpline size={20} className="inline-block " />
                                    </button>
                                  </div>
                  </div>

                <div className="flex-1">
                  {monthlyChartType === 'bar' ? (
                    <div className="h-full">
                      <Bar data={monthlyVisitsData} options={barChartOptions} />
                    </div>
                  ) : (
                    <div style={{ width: "100%", height: "100%" }}>
                      <ReResponsive width="100%" height="100%">
                        <ReLineChart
                          data={monthlyVisitsData.labels.map((m, i) => ({
                            month: m,
                            value: monthlyVisitsData.datasets?.[0]?.data?.[i] ?? 0,
                          }))}
                          margin={{ top: 10, right: 20, left: 0, bottom: 15 }}
                        >
                          <ReCartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#0b1220' : '#f1f5f9'} />
                          <ReXAxis dataKey="month" axisLine={false} tickLine={false} reversed={defaultLanguage === 'ar'} tick={{ fontSize: 12, fill: darkMode ? '#9CA3AF' : '#6B7280', textAnchor: defaultLanguage === 'ar' ? 'end' : 'start' }} />
                          <ReYAxis axisLine={false} tickLine={false} orientation={defaultLanguage === 'ar' ? 'right' : 'left'} tick={{ fontSize: 12, fill: darkMode ? '#9CA3AF' : '#6B7280', textAnchor: defaultLanguage === 'ar' ? 'start' : 'end' }} />
                          <ReTooltip
                            wrapperStyle={{
                              backgroundColor: darkMode ? '#0b1220' : '#fff',
                              border: darkMode ? '1px solid #374151' : '1px solid #e5e7eb',
                              borderRadius: 8,
                              color: darkMode ? '#e5e7eb' : '#0f172a',
                            }}
                          />
                          {/* optional reference areas to visually group ranges similar to BP chart */}
                          <ReReferenceArea y1={0} y2={80} fill="#ecfdf5" fillOpacity={0.3} />
                          <ReReferenceLine y={Math.max(...monthlyVisitsData.datasets[0].data) * 0.75} stroke="#10b981" strokeDasharray="3 3" opacity={0.6}>
                            <ReLabel value={textTranslator('high','STATISTICS')} position="insideTopRight" fill="#059669" fontSize={10} />
                          </ReReferenceLine>

                          <ReLine
                            type="monotone"
                            dataKey="value"
                            stroke="#4f46e5"
                            strokeWidth={3}
                            dot={{ r: 3, strokeWidth: 2, fill: '#fff' }}
                            activeDot={{ r: 6 }}
                            fillOpacity={0.2}
                            name={textTranslator('monthlyVisitsName','STATISTICS')}
                          />
                        </ReLineChart>
                      </ReResponsive>
                    </div>
                  )}
                </div>
              </div>
             </ChartCard>

            <ChartCard
              title={<Text>timeSlotDistribution</Text>}
              description={<Text>preferredVisitTimes</Text>}
              icon={<FaClock size={20} />}
              darkMode={darkMode}
              height="h-56 md:h-64"
            >
              <div className={arClass("h-full flex flex-col justify-center")}>
                <div className="space-y-4">
                  {Object.entries(stats.timeSlots).map(([slot, count], index) => {
                    const percentage = ((count / stats.count) * 100).toFixed(1);
                    const Icon = slot === 'morning' ? FaClock : 
                                slot === 'afternoon' ? FaClock : 
                                slot === 'evening' ? FaClock : FaClock;
                    
                    return (
                      <div key={slot} className={arClass("flex items-center gap-4")}>
                        <div className={arClass(`p-2 rounded-lg ${
                          darkMode ? 'bg-gray-800/60' : 'bg-gray-100'
                        }`)}>
                          <Icon size={16} className={darkMode ? 'text-gray-400' : 'text-gray-600'} />
                        </div>
                          <div className={arClass("flex-1")}>
                          <div className={arClass("flex justify-between mb-1")}>
                            <span className={arClass(`font-medium capitalize ${darkMode ? 'text-gray-300' : 'text-gray-700'}`)}>
                              {textTranslator(slot, 'STATISTICS')}
                            </span>
                            <span className={arClass(`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`)}>
                              {count}
                            </span>
                          </div>
                          <div className={arClass(`h-2 rounded-full overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`)}>
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${percentage}%` }}
                              transition={{ duration: 1, delay: index * 0.1 }}
                              className="h-full rounded-full"
                              style={{ backgroundColor: chartColors[index % chartColors.length] }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </ChartCard>
          </div>

         

          {/* Footer Summary */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className={arClass(`mt-8 p-4 rounded-xl border ${
              darkMode 
                ? 'bg-gradient-to-r from-gray-800/50 to-gray-900/50 border-gray-700' 
                : 'bg-gradient-to-r from-blue-50/50 to-indigo-50/50 border-blue-200'
            }`)}
          >
            <div className={arClass("flex items-center justify-between")}>
              <div className={arClass("flex items-center gap-3")}>
                <div className={arClass(`p-2 rounded-lg ${
                  darkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-600'
                }`)}>
                  <Activity size={18} />
                </div>
                <div>
                  <p className={arClass(`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`)}>
                    <Text>visitSummary</Text>
                  </p>
                  <p className={arClass(`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`)}>
                    {stats.count} <Text>totalVisitsLabel</Text> • {stats.completedVisits} <Text>completedLabel</Text> • {stats.upcomingVisits} <Text>upcomingLabel</Text>
                  </p>
                </div>
              </div>
              
            </div>
          </motion.div>
        </>
      )}
    </motion.div>
  );
}